import {
  ActionIcon,
  Box,
  createStyles,
  Text,
  Center,
  Menu,
  Group,
  Modal,
} from "@mantine/core";
import { IconEdit, IconPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { FaEye } from "react-icons/fa6";
import { LuFileText, LuLayoutTemplate } from "react-icons/lu";
import { RiFilter3Line } from "react-icons/ri";
import { getAllBillingModal, removeBilling, deleteBillingCache } from "../../services";
import { getAllPatients } from "../../services/patient";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EditBilling from "./EditBilling";
import swal from "sweetalert";
import TableLoader from "../TableLoader";
import ClaimFilter from "./ClaimFilter";
import BillingSearchAll from "../Document/BilllingSearchAll";
import RecordNotFound from "../RecordNotFound";
import CustomBillingPagination from "../Pagination/CustomBillingPagination";
import moment from "moment-timezone";
import BillingHistory from "./BillingHistory";
import DraftClaims from "./DraftClaims";
import ClaimTypeChooser from "./ClaimTypeChooser";
import CreateClaimDrawer from "./CreateClaimDrawer";
import ERADetailsDrawer from "./ERADetailsDrawer";
import { decrypt } from "../../utils/index";

const useStyles = createStyles(() => ({
  // ===== KPI Grid =====
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px",
    marginBottom: "18px",
    width: "100%",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  },
  kpi: {
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "12px",
    padding: "18px 20px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  kpiClickablePaid: {
    cursor: "pointer",
    "&:hover": {
      borderColor: "#86E1B0",
      boxShadow: "0 6px 16px rgba(22,163,74,0.12)",
    },
  },
  kpiClickableRejected: {
    cursor: "pointer",
    position: "relative",
    borderColor: "#FCA5A5",
    boxShadow: "0 0 0 1px rgba(252,165,165,0.6), 0 6px 16px rgba(220,38,38,0.10)",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 12,
      right: 12,
      width: 8,
      height: 8,
      borderRadius: "999px",
      background: "#B91C1C",
      animation: "kpiNeedsActionPulse 2s ease-out infinite",
    },
    "@keyframes kpiNeedsActionPulse": {
      "0%, 100%": { boxShadow: "0 0 0 0 rgba(252,165,165,0.55)" },
      "50%":       { boxShadow: "0 0 0 6px rgba(252,165,165,0)" },
    },
    "&:hover": {
      borderColor: "#FCA5A5",
      boxShadow: "0 6px 16px rgba(220,38,38,0.10)",
    },
  },
  kpiLabel: {
    fontSize: "12.5px",
    color: "#667085",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
    letterSpacing: "0",
    lineHeight: 1.4,
  },
  kpiValue: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#171717",
    letterSpacing: "-0.022em",
    fontFamily: "Inter, sans-serif",
    lineHeight: 1.2,
  },
  kpiValueMuted: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#9CA3AF",
    letterSpacing: "-0.022em",
    fontFamily: "Inter, sans-serif",
    lineHeight: 1.2,
  },
  kpiRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "7px",
  },
  kpiRowRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "7px",
  },
  kpiRowLeft: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "7px",
  },
  kpiRowBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "7px",
  },
  kpiMeta: {
    fontSize: "11.5px",
    color: "#9CA3AF",
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
  },
  kpiTrend: {
    fontSize: "12px",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontWeight: 600,
    color: "#15803D",
    fontFamily: "Inter, sans-serif",
  },
  countBadgeLabel: {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#6B7280",
    fontFamily: "Inter, sans-serif",
  },
  countBadgeGood: {
    display: "inline-grid",
    placeItems: "center",
    minWidth: "32px",
    height: "32px",
    padding: "0 8px",
    borderRadius: "999px",
    background: "#DCFCE7",
    color: "#15803D",
    border: "1.5px solid #86E1B0",
    fontSize: "13.5px",
    fontWeight: 800,
    fontFamily: "Inter, sans-serif",
    letterSpacing: "-0.01em",
  },
  countBadgeBad: {
    display: "inline-grid",
    placeItems: "center",
    minWidth: "32px",
    height: "32px",
    padding: "0 8px",
    borderRadius: "999px",
    background: "#FEE2E2",
    color: "#B91C1C",
    border: "1.5px solid #FCA5A5",
    fontSize: "13.5px",
    fontWeight: 800,
    fontFamily: "Inter, sans-serif",
    letterSpacing: "-0.01em",
  },

  // ===== Empty Hero =====
  emptyHero: {
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "14px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
    padding: "56px 40px",
    textAlign: "center",
    marginBottom: "20px",
    "& h3": {
      margin: "0 0 8px",
      fontSize: "22px",
      fontWeight: 700,
      color: "#171717",
      letterSpacing: "-0.018em",
      fontFamily: "Inter, sans-serif",
    },
    "& p": {
      margin: "0 auto 24px",
      maxWidth: "460px",
      fontSize: "14px",
      color: "#667085",
      lineHeight: 1.6,
      fontFamily: "Inter, sans-serif",
    },
  },
  emptyHeroIllu: {
    width: "96px",
    height: "96px",
    margin: "0 auto 22px",
    color: "#B7791F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyHeroBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    marginTop: "20px",
    height: "46px",
    padding: "0 22px",
    background: "#FFBA45",
    color: "#171717",
    border: "1px solid transparent",
    borderRadius: "10px",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background .12s ease, border-color .12s ease, color .12s ease",
    whiteSpace: "nowrap",
    "& svg": { width: "16px", height: "16px" },
    "&:hover": { background: "#EEAA33" },
  },

  emptyHeroTips: {
    marginTop: "36px",
    paddingTop: "28px",
    borderTop: "1px solid #F1F1F1",
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "18px",
    textAlign: "left",
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  emptyTip: { display: "flex", gap: "12px", alignItems: "flex-start" },
  emptyTipIco: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    background: "#FFF4DA",
    color: "#B7791F",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    "& svg": { width: "18px", height: "18px" },
  },
  emptyTipContent: {
    "& h5": {
      margin: "0 0 4px",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#171717",
      fontFamily: "Inter, sans-serif",
    },
    "& p": {
      margin: 0,
      fontSize: "12.5px",
      color: "#667085",
      lineHeight: 1.5,
      fontFamily: "Inter, sans-serif",
    },
  },

  // ===== Claims Card =====
  claimsCard: {
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
    overflow: "hidden",
    marginBottom: "20px",
    width: "100%",
  },
  claimsCardToolbar: {
    padding: "14px 20px",
    borderBottom: "1px solid #F2F2F2",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  filterSegs: {
    display: "flex",
    gap: "6px",
    background: "#F5F5F5",
    padding: "4px",
    borderRadius: "8px",
  },
  filterSeg: {
    padding: "6px 12px",
    borderRadius: "6px",
    border: 0,
    background: "transparent",
    color: "#525252",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
  },
  filterSegActive: {
    background: "#fff",
    color: "#171717",
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
  },
  searchWrap: {
    flex: 1,
    minWidth: "180px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    "& svg": {
      position: "absolute",
      left: "10px",
      width: "15px",
      height: "15px",
      color: "#9CA3AF",
      pointerEvents: "none",
    },
    "& input": {
      width: "100%",
      height: "38px",
      paddingLeft: "34px",
      paddingRight: "12px",
      border: "1px solid #E5E5E5",
      borderRadius: "8px",
      fontSize: "13px",
      fontFamily: "Inter, sans-serif",
      color: "#171717",
      outline: "none",
      background: "#fff",
      "&::placeholder": { color: "#9CA3AF" },
      "&:focus": { borderColor: "#FFAF4A" },
    },
  },
  filterBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#F9FAFB",
    color: "#374151",
    fontWeight: 600,
    fontSize: "13px",
    fontFamily: "Inter, sans-serif",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    padding: "7px 12px",
    cursor: "pointer",
    height: "38px",
    "&:hover": { background: "#F3F4F6" },
  },

  // ===== Claims Table =====
  claimsTblWrap: {
    overflowX: "auto",
    paddingTop: 20,
    paddingInline: 20,
  },
  claimsTbl: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
    "& thead th": {
      background: "#F2F2F2",
      color: "#171717",
      fontWeight: 600,
      fontSize: "12px",
      padding: "12px 14px",
      textAlign: "left",
      whiteSpace: "nowrap",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
    },
    "& thead th:first-of-type": {
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
    },
    "& thead th:last-child": {
      borderTopRightRadius: "8px",
      borderBottomRightRadius: "8px",
    },
    "& thead th.amt": { textAlign: "right" },
    "& tbody td": { padding: "14px", borderBottom: "1px solid #F2F2F2" },
  },

  // Batch row
  batchRow: {
    background: "#fff",
    cursor: "pointer",
    fontWeight: 500,
    transition: "background .12s ease",
    "& td": {
      borderTop: "1px solid #E5E5E5",
      paddingTop: "16px",
      paddingBottom: "16px",
    },
    "& td:first-of-type": {
      width: "36px",
      paddingLeft: "14px",
      paddingRight: 0,
    },
    "&:hover td": { background: "#FAFAFA" },
  },
  batchRowExpanded: {
    "& td": { borderBottom: "1px solid #E5E5E5" },
  },

  thExpand: { width: "36px" },

  // Expand chevron
  expandChev: {
    width: "22px",
    height: "22px",
    border: 0,
    background: "transparent",
    cursor: "pointer",
    color: "#667085",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform .15s ease",
    borderRadius: "4px",
    "& svg": { width: "14px", height: "14px" },
    "&:hover": { background: "rgba(0,0,0,0.06)" },
  },
  expandChevOpen: { transform: "rotate(90deg)" },

  claimId: {
    fontFamily: "ui-monospace, 'SFMono-Regular', Menlo, monospace",
    fontSize: "13px",
    color: "#171717",
    fontWeight: 600,
  },
  batchMeta: {
    fontSize: "11px",
    color: "#667085",
    marginTop: "2px",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
  },
  dimCell: { color: "#737373", fontFamily: "Inter, sans-serif" },
  amtCell: {
    textAlign: "right",
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
    fontFamily: "Inter, sans-serif",
  },

  // Batch status pips
  batchStatus: {
    display: "inline-flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: "132px",
  },
  batchStatusPips: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    flexWrap: "wrap",
  },
  pip: {
    width: "10px",
    height: "10px",
    borderRadius: "3px",
    flexShrink: 0,
    transition: "transform .12s ease",
    "&:hover": { transform: "scale(1.3)", cursor: "help" },
  },
  pipPaid: { background: "#86E1B0" },
  pipRejected: { background: "#FCA5A5" },
  pipProcessing: { background: "#A8C5EA" },
  pipAcknowledged: { background: "#93C5FD" },
  pipDraft: { background: "#D4D4D8" },
  batchStatusSummary: {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#262626",
    lineHeight: 1.35,
    fontFamily: "Inter, sans-serif",
  },
  ctPaid: { color: "#15803D" },
  ctRejected: { color: "#B91C1C" },
  ctProcessing: { color: "#2C5BA3" },
  ctAcknowledged: { color: "#1D4ED8" },
  summaryBold: { fontWeight: 800, marginRight: "3px" },
  summarySep: { color: "#9CA3AF", fontWeight: 400 },

  // Child rows
  childRow: {
    fontSize: "12.5px",
    cursor: "pointer",
    transition: "background .1s ease",
    "& td": {
      color: "#525252",
      borderTop: "1px solid rgba(15,15,20,0.04)",
      paddingTop: "9px",
      paddingBottom: "9px",
    },
    "& td:first-of-type": {
      width: "36px",
      paddingLeft: "14px",
      paddingRight: 0,
    },
    "& td:nth-child(2)": { paddingLeft: "32px", position: "relative" },
    "& td:nth-child(2)::before": {
      content: '""',
      position: "absolute",
      left: "18px",
      top: "50%",
      width: "8px",
      height: "1px",
      background: "rgba(15,15,20,0.18)",
    },
  },
  childRowPaid: {
    background: "#F4FAF6",
    "&:hover td": { background: "#EDF6F0" },
  },
  childRowRejected: {
    background: "#FDF4F2",
    "&:hover td": { background: "#F8E9E5" },
  },
  childRowProcessing: {
    background: "#F2F7FD",
    "&:hover td": { background: "#E5EFFA" },
  },
  childRowAcknowledged: {
    background: "#F5F3FF",
    "&:hover td": { background: "#EDE9FE" },
  },

  // Client cell
  clientCell: { display: "flex", alignItems: "center", gap: "10px" },
  clientAv: {
    width: "36px",
    height: "36px",
    borderRadius: "999px",
    background: "#FFF4DA",
    color: "#B7791F",
    fontSize: "13px",
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    overflow: "hidden",
  },
  clientAvImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "999px",
  },
  clientName: { fontWeight: 500, color: "#171717", fontSize: "13px" },
  reasonRow: {
    fontSize: "12px",
    color: "#B91C1C",
    marginTop: "4px",
    marginLeft: "46px",
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    "& svg": { width: "11px", height: "11px", flexShrink: 0 },
  },

  // Status cell
  statusCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%",
  },

  // Child row menu button
  childRowMenuBtn: {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    background: "transparent",
    border: 0,
    color: "#667085",
    cursor: "pointer",
    display: "inline-grid",
    placeItems: "center",
    "&:hover": { background: "rgba(0,0,0,0.06)", color: "#171717" },
  },
  childRowMenuIcon: { width: "14px", height: "14px" },

  // Pills
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "3px 9px",
    borderRadius: "4px",
    fontSize: "11.5px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    border: "1px solid transparent",
    fontFamily: "Inter, sans-serif",
    "&::before": {
      content: '""',
      width: "6px",
      height: "6px",
      borderRadius: "999px",
      background: "currentColor",
    },
  },
  pillPaid: { color: "#2F7D5C", background: "#F3F8F0", borderColor: "#C6E2C9" },
  pillReject: {
    color: "#B91C1C",
    background: "#FEF2F2",
    borderColor: "#FECDCA",
  },
  pillPending: {
    color: "#2C5BA3",
    background: "#EFF6FF",
    borderColor: "#BFD4F0",
  },
  pillAcknowledged: {
    color: "#6941C6",
    background: "#F9F5FF",
    borderColor: "#D9D0FA",
  },

  // Pager
  pagerWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0px 16px",
    borderTop: "1px solid #E5E5E5",
    background: "#FAFAFA",
    flexWrap: "wrap",
    gap: "10px",
    marginInline: 20,
    marginBottom: 20,
  },
  pagerInfo: {
    fontSize: "12.5px",
    color: "#667085",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
  },
  pagerControls: { display: "inline-flex", alignItems: "center", gap: "4px" },
  pgBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    height: "30px",
    padding: "0 11px",
    borderRadius: "6px",
    border: "1px solid #E5E5E5",
    background: "#fff",
    color: "#374151",
    fontFamily: "Inter, sans-serif",
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
    "&:hover:not(:disabled)": { background: "#FAFAFA", borderColor: "#374151" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  pgBtnNum: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "30px",
    height: "30px",
    padding: "0 8px",
  },
  pgBtnNumActive: {
    background: "#171717",
    color: "#fff",
    borderColor: "#171717",
  },

  // Detail view
  textbox: {
    marginTop: "29px",
    marginBottom: "7px",
    alignSelf: "flex-start",
    marginLeft: "20px",
  },
  detailRow: {
    cursor: "pointer",
    transition: "background .1s ease",
    "&:hover td": { background: "#FAFAFA" },
  },
  notFoundWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    gap: "16px",
  },
  notFoundText: {
    color: "#A8A8A8",
    fontFamily: "Inter",
    fontSize: "15px",
    fontWeight: 400,
    textAlign: "center",
  },

  claimTypeOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    width: "100%",
    textAlign: "left",
    padding: "14px 16px",
    border: "1px solid #E5E5E5",
    borderRadius: "12px",
    background: "#fff",
    cursor: "pointer",
    fontFamily: "Inter",
    transition: "border-color .12s ease, background .12s ease",
    "&:hover": {
      borderColor: "#FAB714",
      background: "#FFFBEB",
    },
  },

  claimTypeIco: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "#FFF4DA",
    color: "#B7791F",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },

  claimTypeInfo: {
    flex: 1,
  },

  claimTypeTitle: {
    margin: "0 0 3px",
    fontSize: "14px",
    fontWeight: 700,
    color: "#171717",
    fontFamily: "Inter",
  },

  claimTypeDesc: {
    margin: 0,
    fontSize: "12.5px",
    color: "#667085",
    fontFamily: "Inter",
    lineHeight: 1.45,
  },

  claimTypeList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "4px 0",
  },
}));

function PreviousClaims({
  setClaimDetails,
  decryptedBillingData,
  setDecryptedBillingData,
}) {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const { allBilling, loading } = useSelector((state) => state?.billing);
  const { patients } = useSelector((state) => state?.patient);

  const [claimTypeModal, setClaimTypeModal] = useState(false);
  const [claimDrawerOpen, setClaimDrawerOpen] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [filtermodal, setFilterModal] = useState(false);
  const [editbilling, SetEditbilling] = useState(false);
  const [billinghistory, SetBillinghistory] = useState(false);
  const [billing, setbilling] = useState({ id: null, claim: null });
  const [view, setView] = useState(false);
  const [billingsearch, setBillingSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedBatches, setExpandedBatches] = useState({});
  const [showDrafts, setShowDrafts] = useState(false);
  const [eraDrawer, setEraDrawer] = useState({
    opened: false,
    claim: null,
    batch: null,
    payerName: "",
    providerName: "",
  });

  const openEraDrawer = (claim, batch, payerName, providerName) => {
    setEraDrawer({ opened: true, claim, batch, payerName, providerName });
  };
  const closeEraDrawer = () =>
    setEraDrawer((prev) => ({ ...prev, opened: false }));

  const ITEMS_PER_PAGE = 10;

  // Map UI filter label → backend status value
  const filterToStatus = {
    All: null,
    Pending: "pending",
    Acknowledged: "A",
    Paid: "P",
    Rejected: "R",
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBilling(page, activeFilter, billingsearch);
  };

  const fetchBilling = (page = 1, filter = activeFilter, search = billingsearch) => {
    getAllBillingModal({
      page,
      status: filterToStatus[filter] || undefined,
      name: search || undefined,
    });
  };

  useEffect(() => {
    fetchBilling(1);
    getAllPatients("all", true, true);
  }, []);

  // Refetch when filter changes — immediate
  useEffect(() => {
    setCurrentPage(1);
    fetchBilling(1, activeFilter, billingsearch);
  }, [activeFilter]);

  // Refetch when search changes — debounced 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchBilling(1, activeFilter, billingsearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [billingsearch]);

  const openEditModal = (id, claim) => {
    setView(false);
    setbilling({ id, claim });
    SetEditbilling(true);
  };
  const openViewModal = (id, claim) => {
    setbilling({ id, claim });
    setView(true);
    SetEditbilling(true);
  };
  const openHistoryModal = (id, claim) => {
    setbilling({ id, claim });
    SetBillinghistory(true);
  };

  const remove = async (id, claimid) => {
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) removeBilling({ id, claimid });
  };

  const handleIconClick = (billingItem) => {
    setDecryptedBillingData(billingItem);
    setClaimDetails(true);
  };


  const toggleBatch = (id) =>
    setExpandedBatches((prev) => ({ ...prev, [id]: !prev[id] }));

  // KPI derivations
  const billingList =
    allBilling?.Billing?.filter((item) =>
      item?.billingForm?.claim?.some((c) => c.deleted !== true),
    ) || [];
  const hasClaims = (allBilling?.totalCount ?? allBilling?.count ?? billingList.length) > 0;
  const allClaims = billingList.flatMap((b) => b?.billingForm?.claim || []);
  const paidCount = allBilling?.paidCount ?? allClaims.filter((c) => c?.status === "P").length;
  const rejectedCount = allBilling?.rejectedCount ?? allClaims.filter((c) => c?.status === "R").length;
  const submittedCountToday = allBilling?.submittedCountToday ?? 0;
  const paidCountToday = allBilling?.paidCountToday ?? 0;
  const rejectedCountToday = allBilling?.rejectedCountToday ?? 0;
  const submittedCount = allBilling?.totalCount ?? allBilling?.count ?? billingList.length;
  const totalAmount = allBilling?.totalAmount ?? allBilling?.totalAmountThisMonth ?? allClaims.reduce(
    (sum, c) =>
      sum +
      (c?.charge?.reduce((s, ch) => s + (parseFloat(ch?.charge) || 0), 0) || 0),
    0,
  );
  const paidAmount = allBilling?.paidAmount ?? allClaims
    .filter((c) => c?.status === "P")
    .reduce((sum, c) => sum + (c?.charge?.reduce((s, ch) => s + (parseFloat(ch?.charge) || 0), 0) || 0), 0);
  const fmtAmount = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


  const getStatusPill = (status) => {
    if (status === "P")
      return <span className={cx(classes.pill, classes.pillPaid)}>Paid</span>;
    if (status === "R")
      return (
        <span className={cx(classes.pill, classes.pillReject)}>Rejected</span>
      );
    if (status === "A")
      return (
        <span className={cx(classes.pill, classes.pillAcknowledged)}>
          Acknowledged
        </span>
      );
    return (
      <span className={cx(classes.pill, classes.pillPending)}>Pending</span>
    );
  };

  const getInitials = (fname, lname) =>
    `${(fname || "")[0] || ""}${(lname || "")[0] || ""}`.toUpperCase() || "?";

  const getClientImage = (claim) => {
    try {
      const allPats = patients?.patients || [];
      const matched = allPats.find(
        (p) =>
          p?.form1?.firstName?.toLowerCase() === (claim?.pat_name_f || "").toLowerCase() &&
          p?.form1?.lastName?.toLowerCase() === (claim?.pat_name_l || "").toLowerCase()
      );
      const raw = matched?.imageurl;
      if (!raw) return null;
      return decrypt(raw) || null;
    } catch {
      return null;
    }
  };

  const getStatusClass = (status) => {
    if (status === "P") return classes.childRowPaid;
    if (status === "R") return classes.childRowRejected;
    if (status === "A") return classes.childRowAcknowledged;
    return classes.childRowProcessing;
  };

  const getPipClass = (status) => {
    if (status === "P") return classes.pipPaid;
    if (status === "R") return classes.pipRejected;
    if (status === "A") return classes.pipAcknowledged;
    return classes.pipProcessing;
  };

  const getBatchSummaryParts = (claims) => {
    const p = claims.filter((c) => c?.status === "P").length;
    const r = claims.filter((c) => c?.status === "R").length;
    const a = claims.filter((c) => c?.status === "A").length;
    const pend = claims.filter((c) => c?.status !== "P" && c?.status !== "R" && c?.status !== "A").length;
    return { p, r, a, pend };
  };

  return (
    <>
      {/* ===== KPI Grid ===== */}
      <div className={classes.kpiGrid}>
        {/* Submitted */}
        <div className={classes.kpi}>
          <span className={classes.kpiLabel}>Submitted</span>
          <div className={hasClaims ? classes.kpiValue : classes.kpiValueMuted}>
            {hasClaims ? submittedCount : "0"}
          </div>
          <div className={hasClaims ? classes.kpiRowBetween : classes.kpiRowLeft}>
            {hasClaims ? (
              <>
                {submittedCountToday > 0 ? (
                  <span className={classes.kpiTrend}>▲ {submittedCountToday} new</span>
                ) : (
                  <span className={classes.kpiMeta}>—</span>
                )}
                <span className={classes.kpiMeta}>{fmtAmount(totalAmount)}</span>
              </>
            ) : (
              <span className={classes.kpiMeta}>No claims yet</span>
            )}
          </div>
        </div>

        {/* Paid */}
        <div
          className={cx(classes.kpi, hasClaims && classes.kpiClickablePaid)}
          onClick={() => { if (!hasClaims) return; navigate("/home/billing-paid"); }}
        >
          <span className={classes.kpiLabel}>Paid</span>
          <div className={hasClaims ? classes.kpiValue : classes.kpiValueMuted}>
            {hasClaims ? fmtAmount(paidAmount) : "$0.00"}
          </div>
          <div className={hasClaims ? classes.kpiRowRight : classes.kpiRowLeft}>
            {hasClaims ? (
              paidCountToday > 0 ? (
                <>
                  <span className={classes.countBadgeLabel}>today</span>
                  <span className={classes.countBadgeGood}>{paidCountToday}</span>
                </>
              ) : null
            ) : (
              <span className={classes.kpiMeta}>No payments yet</span>
            )}
          </div>
        </div>

        {/* Rejected */}
        <div
          className={cx(
            classes.kpi,
            hasClaims && rejectedCountToday > 0 && classes.kpiClickableRejected,
            hasClaims && rejectedCountToday === 0 && classes.kpiResolved,
          )}
          onClick={() => { if (!hasClaims) return; navigate("/home/billing-rejected"); }}
        >
          <span className={classes.kpiLabel}>Rejected</span>
          <div className={hasClaims ? classes.kpiValue : classes.kpiValueMuted}>
            {hasClaims ? rejectedCount : "0"}
          </div>
          <div className={hasClaims ? classes.kpiRowRight : classes.kpiRowLeft}>
            {hasClaims ? (
              rejectedCountToday > 0 ? (
                <>
                  <span className={classes.countBadgeLabel}>today</span>
                  <span className={classes.countBadgeBad}>{rejectedCountToday}</span>
                </>
              ) : null
            ) : (
              <span className={classes.kpiMeta}>No rejections</span>
            )}
          </div>
        </div>
      </div>

      {/* ===== Empty Hero ===== */}
      {!showDrafts && !hasClaims && !loading && (
        <div className={classes.emptyHero}>
          <div className={classes.emptyHeroIllu}>
            <svg
              width="96"
              height="96"
              viewBox="0 0 96 96"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect
                x="20"
                y="14"
                width="48"
                height="60"
                rx="6"
                fill="#FFF7ED"
                stroke="#FFBA45"
              />
              <path d="M28 28h28M28 38h28M28 48h20" stroke="#B7791F" />
              <circle cx="68" cy="64" r="14" fill="#FFFBEB" stroke="#FFBA45" />
              <path d="M62 64h12M68 58v12" stroke="#B7791F" strokeWidth="2.5" />
            </svg>
          </div>
          <h3>You haven&apos;t submitted any claims yet</h3>
          <p>
            Once you submit your first claim, payments, denials, and updates
            will start showing up here. Most claims get a payer response within
            7–14 days.
          </p>
          <button className={classes.emptyHeroBtn} onClick={() => setClaimTypeModal(true)}>
            <IconPlus size={16} />
            Submit your first claim
          </button>
          <div className={classes.emptyHeroTips}>
            <div className={classes.emptyTip}>
              <div className={classes.emptyTipIco}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 12A10 10 0 1 1 12 2" />
                  <path d="M22 4 12 14.01l-3-3" />
                </svg>
              </div>
              <div className={classes.emptyTipContent}>
                <h5>Verify eligibility</h5>
                <p>
                  Run a real-time eligibility check before billing to avoid
                  denials.
                </p>
              </div>
            </div>
            <div className={classes.emptyTip}>
              <div className={classes.emptyTipIco}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M9 13l2 2 4-4" />
                </svg>
              </div>
              <div className={classes.emptyTipContent}>
                <h5>Use a claim template</h5>
                <p>
                  Service codes and rates auto-populate from the provider you
                  set up.
                </p>
              </div>
            </div>
            <div className={classes.emptyTip}>
              <div className={classes.emptyTipIco}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <path d="M7 10l5 5 5-5" />
                  <path d="M12 15V3" />
                </svg>
              </div>
              <div className={classes.emptyTipContent}>
                <h5>Track ERAs automatically</h5>
                <p>
                  When payers pay, the ERA shows up here with adjustments and
                  CARC codes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Claims Card — filled state ===== */}
      {(hasClaims || loading || showDrafts) &&
        decryptedBillingData === null && (
          <div className={classes.claimsCard}>
            {/* Toolbar */}
            <div className={classes.claimsCardToolbar}>
              <div className={classes.filterSegs}>
                {["All", "Pending", "Acknowledged", "Paid", "Rejected"].map(
                  (seg) => (
                    <button
                      key={seg}
                      className={cx(
                        classes.filterSeg,
                        !showDrafts &&
                          activeFilter === seg &&
                          classes.filterSegActive,
                      )}
                      onClick={() => {
                        setShowDrafts(false);
                        setActiveFilter(seg);
                      }}
                    >
                      {seg}
                    </button>
                  ),
                )}
                <button
                  className={cx(
                    classes.filterSeg,
                    showDrafts && classes.filterSegActive,
                  )}
                  onClick={() => {
                    setShowDrafts(true);
                  }}
                >
                  Drafts
                </button>
              </div>
              <div className={classes.searchWrap}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  placeholder="Search claim ID, client or payer"
                  value={billingsearch}
                  onChange={(e) => setBillingSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Drafts view */}
            {showDrafts && (
              <DraftClaims
                setClaimDetails={setClaimDetails}
                decryptedBillingData={decryptedBillingData}
                setDecryptedBillingData={setDecryptedBillingData}
                onContinueDraft={(item) => {
                  setDraftData(item);
                  setClaimDrawerOpen(true);
                }}
                onDeleteDraft={(id) => deleteBillingCache(id)}
              />
            )}

            {/* Table + Pagination (submitted view) */}
            {!showDrafts && (
              <>
                <div className={classes.claimsTblWrap}>
                  {loading ? (
                    <TableLoader />
                  ) : (
                    <table className={classes.claimsTbl}>
                      <thead>
                        <tr>
                          <th className={classes.thExpand}></th>
                          <th>Claim</th>
                          <th>Client</th>
                          <th>Payer</th>
                          <th>Provider</th>
                          <th className="amt">Amount</th>
                          <th>Submitted</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingList.length > 0 ? billingList.map((item, index) => {
                          console.log("🚀 ~ PreviousClaims ~ item:", item)
                          const batchId = item?._id || index;
                          const isOpen = !!expandedBatches[batchId];
                          const claims = item?.billingForm?.claim || [];
                          const totalCharge = claims
                            .flatMap((c) => c.charge || [])
                            .reduce(
                              (t, ch) => t + (parseFloat(ch?.charge) || 0),
                              0,
                            );
                          const payerName = claims[0]?.payer_name || "—";
                          const providerName = claims[0]?.prov_name_m || "—";
                          const batchClaimId =
                            item?.batchId ||
                            `batch-${(item?._id || "").toString().slice(-6)}`;
                          const submittedDate = item?.createdAt
                            ? new Date(item.createdAt).toLocaleDateString(
                                "en-US",
                              )
                            : "—";
                          const { p, r, a, pend } = getBatchSummaryParts(claims);

                          return [
                            // ===== Batch row =====
                            <tr
                              key={`batch-${batchId}`}
                              className={cx(
                                classes.batchRow,
                                isOpen && classes.batchRowExpanded,
                              )}
                              onClick={() => toggleBatch(batchId)}
                            >
                              <td>
                                <button
                                  type="button"
                                  className={cx(
                                    classes.expandChev,
                                    isOpen && classes.expandChevOpen,
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBatch(batchId);
                                  }}
                                  aria-label="Expand batch"
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="m9 6 6 6-6 6" />
                                  </svg>
                                </button>
                              </td>
                              <td>
                                <span className={classes.claimId}>
                                  {batchClaimId}
                                </span>
                                <div className={classes.batchMeta}>
                                  {claims.length} claim
                                  {claims.length !== 1 ? "s" : ""} ·{" "}
                                  {submittedDate}
                                </div>
                              </td>
                              <td>
                                {claims.length} client
                                {claims.length !== 1 ? "s" : ""}
                              </td>
                              <td className={classes.dimCell}>{payerName}</td>
                              <td className={classes.dimCell}>
                                {providerName}
                              </td>
                              <td className={classes.amtCell}>
                                ${totalCharge.toLocaleString("en-US")}
                              </td>
                              <td className={classes.dimCell}>
                                {submittedDate}
                              </td>
                              <td>
                                <div className={classes.batchStatus}>
                                  <div className={classes.batchStatusPips}>
                                    {claims.map((c, ci) => (
                                      <span
                                        key={ci}
                                        className={cx(
                                          classes.pip,
                                          getPipClass(c?.status),
                                        )}
                                        title={`Claim ${ci + 1}: ${c?.status === "P" ? "Paid" : c?.status === "R" ? "Rejected" : c?.status === "A" ? "Acknowledged" : "Pending"}`}
                                      />
                                    ))}
                                  </div>
                                  <div className={classes.batchStatusSummary}>
                                    {p > 0 && (
                                      <span className={classes.ctPaid}>
                                        <b className={classes.summaryBold}>
                                          {p}
                                        </b>
                                        paid
                                      </span>
                                    )}
                                    {p > 0 && r > 0 && (
                                      <span className={classes.summarySep}>
                                        ·
                                      </span>
                                    )}
                                    {r > 0 && (
                                      <span className={classes.ctRejected}>
                                        <b className={classes.summaryBold}>
                                          {r}
                                        </b>
                                        rejected
                                      </span>
                                    )}
                                    {(p > 0 || r > 0) && a > 0 && (
                                      <span className={classes.summarySep}>
                                        ·
                                      </span>
                                    )}
                                    {a > 0 && (
                                      <span className={classes.ctAcknowledged}>
                                        <b className={classes.summaryBold}>
                                          {a}
                                        </b>
                                        acknowledged
                                      </span>
                                    )}
                                    {(p > 0 || r > 0 || a > 0) && pend > 0 && (
                                      <span className={classes.summarySep}>
                                        ·
                                      </span>
                                    )}
                                    {pend > 0 && (
                                      <span className={classes.ctProcessing}>
                                        <b className={classes.summaryBold}>
                                          {pend}
                                        </b>
                                        pending
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>,

                            // ===== Child rows =====
                            ...(isOpen
                              ? claims.map((claim, ci) => {
                                  const fromDate =
                                    claim?.charge?.filter((i) => i.from_date)
                                      .length > 0
                                      ? moment
                                          .min(
                                            claim.charge
                                              .filter((i) => i.from_date)
                                              .map((i) =>
                                                moment.tz(
                                                  i.from_date,
                                                  "YYYY-MM-DD",
                                                  moment.tz.guess(),
                                                ),
                                              ),
                                          )
                                          .format("MM/DD/YYYY")
                                      : null;

                                  return (
                                    <tr
                                      key={`child-${batchId}-${ci}`}
                                      className={cx(
                                        classes.childRow,
                                        getStatusClass(claim?.status),
                                      )}
                                      onClick={() => {
                                        if (claim?.status === "R") {
                                          navigate("/home/billing-rejected", { state: { claim, batch: item } });
                                          return;
                                        }
                                        openEraDrawer(
                                          claim,
                                          item,
                                          payerName,
                                          providerName,
                                        );
                                      }}
                                    >
                                      <td></td>
                                      <td>
                                        <span className={classes.claimId}>
                                          {claim?.claimmd_id ? `CLM-${claim.claimmd_id}` : `CLM-${ci + 1}`}
                                        </span>
                                      </td>
                                      <td>
                                        <div className={classes.clientCell}>
                                          <span className={classes.clientAv}>
                                            {getClientImage(claim) ? (
                                              <img
                                                src={getClientImage(claim)}
                                                alt={`${claim?.pat_name_f} ${claim?.pat_name_l}`}
                                                className={classes.clientAvImg}
                                              />
                                            ) : (
                                              getInitials(
                                                claim?.pat_name_f,
                                                claim?.pat_name_l,
                                              )
                                            )}
                                          </span>
                                          <span className={classes.clientName}>
                                            {claim?.pat_name_f}{" "}
                                            {claim?.pat_name_l}
                                          </span>
                                        </div>
                                        {claim?.status === "R" && (
                                          <div className={classes.reasonRow}>
                                            <svg
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2.4"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                                              <path d="M12 9v4M12 17h.01" />
                                            </svg>
                                            {claim?.errors?.length > 0
                                              ? claim.errors
                                                  .filter((e) => (e?.message || "").toLowerCase() !== "acknowledged")
                                                  .map((e) => e.message)
                                                  .filter(Boolean)
                                                  .join("; ") ||
                                                "Unknown Error"
                                              : "Unknown Error"}
                                          </div>
                                        )}
                                      </td>
                                      <td className={classes.dimCell}>
                                        {payerName}
                                      </td>
                                      <td className={classes.dimCell}>
                                        {providerName}
                                      </td>
                                      <td className={classes.amtCell}>
                                        $
                                        {(
                                          claim?.total_charge ||
                                          (claim?.charge || []).reduce(
                                            (t, ch) =>
                                              t + (parseFloat(ch?.charge) || 0),
                                            0,
                                          )
                                        ).toLocaleString("en-US")}
                                      </td>
                                      <td className={classes.dimCell}>
                                        {fromDate || submittedDate}
                                      </td>
                                      <td>
                                        <div className={classes.statusCell}>
                                          {getStatusPill(claim?.status)}
                                          <Menu
                                            shadow="md"
                                            position="left"
                                            zIndex={1000}
                                          >
                                            <Menu.Target>
                                              <button
                                                className={
                                                  classes.childRowMenuBtn
                                                }
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <svg
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  className={
                                                    classes.childRowMenuIcon
                                                  }
                                                >
                                                  <circle
                                                    cx="12"
                                                    cy="5"
                                                    r="1"
                                                  />
                                                  <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="1"
                                                  />
                                                  <circle
                                                    cx="12"
                                                    cy="19"
                                                    r="1"
                                                  />
                                                </svg>
                                              </button>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                              {claim?.status === "P" && (
                                                <Menu.Item
                                                  icon={<LuFileText size={14} />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openEraDrawer(claim, item, payerName, providerName);
                                                  }}
                                                >
                                                  View ERA
                                                </Menu.Item>
                                              )}
                                              {claim?.status !== "P" && claim?.status !== "R" && (
                                                <Menu.Item
                                                  icon={<LuFileText size={14} />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openEraDrawer(claim, item, payerName, providerName);
                                                  }}
                                                >
                                                  View Claim
                                                </Menu.Item>
                                              )}
                                              {claim?.status === "P" && (
                                                <Menu.Item
                                                  icon={<LuLayoutTemplate size={14} />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate("/home/billing-paid", { state: { claim, batch: item } });
                                                  }}
                                                >
                                                  View CMS-1500
                                                </Menu.Item>
                                              )}
                                              {claim?.status === "R" && (
                                                <Menu.Item
                                                  icon={<LuLayoutTemplate size={14} />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate("/home/billing-rejected", { state: { claim, batch: item } });
                                                  }}
                                                >
                                                  View CMS-1500
                                                </Menu.Item>
                                              )}
                                              {claim?.status !== "P" && claim?.status !== "R" && (
                                                <Menu.Item
                                                  icon={<LuLayoutTemplate size={14} />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate("/home/billing-paid", { state: { claim, batch: item } });
                                                  }}
                                                >
                                                  View CMS-1500
                                                </Menu.Item>
                                              )}
                                            </Menu.Dropdown>
                                          </Menu>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              : []),
                          ];
                        }) : (
                          <tr>
                            <td colSpan={8} style={{ textAlign: "center", color: "#A8A8A8", padding: "60px 20px", fontFamily: "Inter", fontSize: "15px" }}>
                              No billing records found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination */}
                <div className={classes.pagerWrap}>
                  <div className={classes.pagerInfo}>
                    {allBilling?.count
                      ? `Showing ${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(currentPage * ITEMS_PER_PAGE, allBilling.count)} of ${allBilling.count} batches`
                      : ""}
                  </div>
                  <Center>
                    <CustomBillingPagination
                      data={allBilling?.count}
                      itemsPerPage={ITEMS_PER_PAGE}
                      currentPage={currentPage}
                      handlePageChange={handlePageChange}
                    />
                  </Center>
                </div>
              </>
            )}
          </div>
        )}

      {/* ===== Detail view ===== */}
      {decryptedBillingData !== null && (
        <>
          <Box className={classes.textbox}>
            <Text ff={"Inter"} fz={"16px"} fw={"700"} color={"#000"}>
              {decryptedBillingData.billingForm.claim[0].prov_name_m}
            </Text>
          </Box>
          <div className={classes.claimsCard}>
            <div className={classes.claimsTblWrap}>
              <table className={classes.claimsTbl}>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Client Name</th>
                    <th>Claim ID</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th className="amt">Total Charge</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {decryptedBillingData?.billingForm?.claim?.map(
                    (claim, ci) => {
                      const fromDate =
                        claim?.charge?.filter((i) => i.from_date).length > 0
                          ? moment
                              .min(
                                claim.charge
                                  .filter((i) => i.from_date)
                                  .map((i) =>
                                    moment.tz(
                                      i.from_date,
                                      "YYYY-MM-DD",
                                      moment.tz.guess(),
                                    ),
                                  ),
                              )
                              .format("MM/DD/YYYY")
                          : "—";
                      const toDate =
                        claim?.charge?.filter((i) => i.thru_date).length > 0
                          ? moment
                              .max(
                                claim.charge
                                  .filter((i) => i.thru_date)
                                  .map((i) =>
                                    moment.tz(
                                      i.thru_date,
                                      "YYYY-MM-DD",
                                      moment.tz.guess(),
                                    ),
                                  ),
                              )
                              .format("MM/DD/YYYY")
                          : "—";

                      return (
                        <tr key={ci} className={classes.detailRow}>
                          <td>{getStatusPill(claim?.status)}</td>
                          <td className={classes.dimCell}>
                            {claim.pat_name_f} {claim.pat_name_l}
                          </td>
                          <td>
                            <span className={classes.claimId}>
                              {claim?.claimmd_id || "—"}
                            </span>
                          </td>
                          <td className={classes.dimCell}>{fromDate}</td>
                          <td className={classes.dimCell}>{toDate}</td>
                          <td className={classes.amtCell}>
                            $
                            {claim?.total_charge?.toLocaleString("en-US") ||
                              "N/A"}
                          </td>
                          <td>
                            <Group>
                              {claim?.status !== "A" && <Menu shadow="md" position="left" zIndex={1000}>
                                <Menu.Target>
                                  <ActionIcon>
                                    <BiDotsVerticalRounded
                                      size={20}
                                      color="#000"
                                    />
                                  </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  {claim?.status === "P" && (
                                    <Menu.Item
                                      icon={<LuFileText size={14} />}
                                      onClick={() =>
                                        openEraDrawer(
                                          claim,
                                          decryptedBillingData,
                                          claim?.payer_name || "—",
                                          claim?.prov_name_m || "—",
                                        )
                                      }
                                    >
                                      View ERA
                                    </Menu.Item>
                                  )}
                                  {claim?.status !== "P" && claim?.status !== "R" && (
                                    <Menu.Item
                                      icon={<LuFileText size={14} />}
                                      onClick={() =>
                                        openEraDrawer(
                                          claim,
                                          decryptedBillingData,
                                          claim?.payer_name || "—",
                                          claim?.prov_name_m || "—",
                                        )
                                      }
                                    >
                                      View Claim
                                    </Menu.Item>
                                  )}
                                  {(claim?.status === "P" || claim?.status === "R") && (
                                    <Menu.Item
                                      icon={<LuLayoutTemplate size={14} />}
                                      onClick={() => {
                                        if (claim?.status === "P") navigate("/home/billing-paid", { state: { claim, batch: decryptedBillingData } });
                                        else navigate("/home/billing-rejected", { state: { claim, batch: decryptedBillingData } });
                                      }}
                                    >
                                      View CMS-1500
                                    </Menu.Item>
                                  )}
                                </Menu.Dropdown>
                              </Menu>}
                            </Group>
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
            <div className={classes.pagerWrap}>
              <div className={classes.pagerInfo}></div>
              <Center>
                <CustomBillingPagination
                  data={decryptedBillingData?.billingForm?.claim?.length}
                  itemsPerPage={10}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                />
              </Center>
            </div>
          </div>

          <EditBilling
            editbilling={editbilling}
            SetEditbilling={SetEditbilling}
            billing={billing}
            view={view}
            setDecryptedBillingData={setDecryptedBillingData}
          />
          <BillingHistory
            billinghistory={billinghistory}
            SetBillinghistory={SetBillinghistory}
            billing={billing}
          />
        </>
      )}

      <ClaimTypeChooser
        open={claimTypeModal}
        onClose={() => setClaimTypeModal(false)}
        onProfessional={() => setClaimDrawerOpen(true)}
        onInstitutional={() => {}}
      />

      <CreateClaimDrawer
        opened={claimDrawerOpen}
        onClose={() => { setClaimDrawerOpen(false); setDraftData(null); }}
        draftData={draftData}
      />

      <ClaimFilter filtermodal={filtermodal} setFilterModal={setFilterModal} />

      <ERADetailsDrawer
        opened={eraDrawer.opened}
        onClose={closeEraDrawer}
        claim={eraDrawer.claim}
        batch={eraDrawer.batch}
        payerName={eraDrawer.payerName}
        providerName={eraDrawer.providerName}
      />
    </>
  );
}

export default PreviousClaims;
